import { Badge, Button, Checkbox, Label, Textarea } from '@design-system';
import {
  EmailTagInput,
  AdditionalSettingsButton,
  useInviteClientStore,
} from '@/features/invite-client';

import { Check, Loader2, Pencil, Send, X } from 'lucide-react';

import { useState } from 'react';

export function BulkEmailTab() {
  const {
    inviteClientConfig,
    validBulkEmails,
    canContinueBulk,
    isLoading,
    handleSendEmail,
    handleConfigChange,
    handleClearBulkEmails,
  } = useInviteClientStore();

  const [isEditing, setIsEditing] = useState(false);

  const { bulkEmails, isComplianceConfirmed, emailBody } = inviteClientConfig;

  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-base font-semibold text-gray-950">Invite multiple clients</h4>
        <p className="text-foreground text-sm">
          Add multiple email addresses to send invitations in bulk. Press Enter or comma after each
          email.
        </p>
      </div>

      <div className="space-y-2">
        <EmailTagInput
          value={bulkEmails}
          onChange={value => handleConfigChange('bulkEmails', value)}
          maxEmails={35}
        />
        {bulkEmails.length > 0 && (
          <div className="flex justify-end">
            <Badge
              variant="destructive"
              className="cursor-pointer"
              onClick={handleClearBulkEmails}
            >
              <X className="size-3" />
              <span className="pr-1">Clear all</span>
            </Badge>
          </div>
        )}
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="cursor-default">Email template</Label>
          <Badge
            variant="secondary"
            className="cursor-pointer"
            onClick={() => setIsEditing(!isEditing)}
          >
            {isEditing ? (
              <>
                <Check className="size-3" />
                Save
              </>
            ) : (
              <>
                <Pencil className="size-3" />
                Edit
              </>
            )}
          </Badge>
        </div>
        {isEditing ? (
          <Textarea
            value={emailBody}
            onChange={({ target }) => handleConfigChange('emailBody', target.value)}
            rows={6}
            className="text-sm"
          />
        ) : (
          <div className="bg-muted rounded-lg p-3">
            <p className="text-foreground line-clamp-3 text-sm">"{emailBody}"</p>
          </div>
        )}
      </div>

      <div className="flex items-start gap-3">
        <Checkbox
          id="compliance"
          checked={isComplianceConfirmed}
          onCheckedChange={checked => handleConfigChange('isComplianceConfirmed', !!checked)}
          className="translate-y-0.5"
        />
        <Label
          htmlFor="compliance"
          className="text-foreground text-sm italic"
        >
          I confirm that I have either obtained compliance approval or understand this message may
          require compliance review. I acknowledge that sending this email is my responsibility.
        </Label>
      </div>

      <div className="flex gap-3">
        <AdditionalSettingsButton className="flex-1" />
        <Button
          className="flex-1"
          onClick={() => handleSendEmail(true)}
          disabled={!canContinueBulk || isLoading}
        >
          {isLoading ? <Loader2 className="animate-spin" /> : <Send />}
          {isLoading
            ? 'Sending...'
            : `Send ${validBulkEmails.length} invitation${validBulkEmails.length !== 1 ? 's' : ''}`}
        </Button>
      </div>
    </div>
  );
}
